import sanitizeHtml from 'sanitize-html';

/** Same CID-image policy as the inbound worker; preserve embedded tickets without remote fetches. */
export async function originalEmailPreview(bytes: ArrayBuffer): Promise<string> {
  const { default: PostalMime } = await import('postal-mime');
  const email = await PostalMime.parse(bytes);
  let html = email.html || `<pre>${(email.text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
  for (const attachment of email.attachments) {
    if (!attachment.contentId || !/^image\/(png|jpeg|gif|webp)$/.test(attachment.mimeType)) continue;
    let binary = '';
    for (const byte of new Uint8Array(attachment.content as ArrayBuffer)) binary += String.fromCharCode(byte);
    html = html.split(`cid:${attachment.contentId.replace(/^<|>$/g, '')}`)
      .join(`data:${attachment.mimeType};base64,${btoa(binary)}`);
  }
  return emailPreviewHtml(html);
}

/** Read-only email: no scripts, navigation, forms, remote images or CSS fetches. */
export function emailPreviewHtml(html: string): string {
  const clean = sanitizeHtml(html, {
    allowedTags: [...sanitizeHtml.defaults.allowedTags, 'img'],
    allowedAttributes: {
      '*': ['style', 'align'], table: ['width', 'cellpadding', 'cellspacing', 'border'],
      td: ['width', 'colspan', 'rowspan'], img: ['src', 'alt', 'width', 'height'],
    },
    allowedSchemes: ['data'],
    allowedStyles: { '*': {
      color: [/^(#[0-9a-f]{3,8}|[a-z]+|rgba?\([\d.,%\s]+\))$/i],
      'background-color': [/^(#[0-9a-f]{3,8}|[a-z]+|rgba?\([\d.,%\s]+\))$/i],
      'font-size': [/^[\d.]+(px|pt|em|rem|%)$/],
      'font-family': [/^[\w\s,'"-]+$/], 'font-weight': [/^(normal|bold|[1-9]00)$/],
      'text-align': [/^(left|right|center|justify)$/],
      padding: [/^[\d.\s]+(px|pt|em|rem|%)$/],
      width: [/^[\d.]+(px|%)$/], 'max-width': [/^[\d.]+(px|%)$/],
    } },
    transformTags: { img: (_tag, attrs) => ({ tagName: 'img', attribs:
      /^data:image\/(png|jpeg|gif|webp);base64,/i.test(attrs.src ?? '') ? attrs : { alt: attrs.alt ?? '' } }) },
  });
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'"><meta name="viewport" content="width=device-width, initial-scale=1"><style>body{margin:16px;font-family:Arial,sans-serif;overflow-wrap:anywhere}img,table{max-width:100%;height:auto}</style></head><body>${clean}</body></html>`;
}
